"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { ClinicLink } from "@/components/clinic/clinic-link";
import { resolveBg } from "@/lib/bg-color";
import { type Clinic } from "@/lib/clinics";

export type ClinicGridBlok = SbBlokData & {
  title?: string;
  body?: string;
  clinics?: string[];
  bg_color?: unknown;
};

type Filter = "all" | "ireland" | "uk";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All clinics" },
  { id: "ireland", label: "Ireland" },
  { id: "uk", label: "United Kingdom" },
];

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClinicCard({
  clinic,
  cardRef,
}: {
  clinic: Clinic;
  cardRef: (el: HTMLLIElement | null) => void;
}) {
  const address = [clinic.addressLine1, clinic.addressLine2].filter(Boolean).join(", ");
  return (
    <li ref={cardRef} className="w-[82%] shrink-0 snap-start sm:w-auto">
      <ClinicLink clinic={clinic} className="group flex h-full flex-col rounded-[20px]">
        <div className="bg-dusk-01 relative aspect-4/3 w-full overflow-hidden rounded-[20px]">
          {clinic.photo ? (
            <Image
              src={clinic.photo.filename}
              alt={clinic.photo.alt || clinic.name}
              fill
              sizes="(min-width: 1115px) 360px, (min-width: 640px) 50vw, 100vw"
              className="object-cover"
            />
          ) : (
            <div className="from-dusk-02 to-purple-01 absolute inset-0 bg-linear-to-br" />
          )}
          {clinic.flagship && (
            <span className="text-purple-05 absolute top-3 right-3 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tracking-wide uppercase">
              Primary
            </span>
          )}
        </div>

        <div className="relative mx-4 -mt-10 flex flex-1 flex-col gap-2 rounded-[16px] border border-black/5 bg-white p-5 sm:mx-5 sm:-mt-12 sm:p-6">
          <h3 className="text-h6 text-purple-05 leading-[1.2] font-bold">{clinic.name}</h3>
          {address && <p className="text-body leading-normal text-black/65">{address}</p>}
          <span className="text-body text-purple-03 mt-auto inline-flex items-center gap-[7px] pt-2 leading-[1.054] font-extrabold group-hover:underline lg:-translate-y-0.5">
            View clinic
            <ArrowIcon />
          </span>
        </div>
      </ClinicLink>
    </li>
  );
}

export default function ClinicGridView({
  blok,
  clinics,
}: {
  blok: ClinicGridBlok;
  clinics: Clinic[];
}) {
  const { bg, onDark } = resolveBg(blok.bg_color, "dusk-01");
  const [filter, setFilter] = useState<Filter>("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const scrollerRef = useRef<HTMLUListElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Filters only make sense when the clinics span both countries.
  const hasIreland = clinics.some((c) => c.country === "Ireland");
  const hasUk = clinics.some((c) => c.country === "United Kingdom");
  const showFilters = hasIreland && hasUk;

  const pathname = usePathname();
  const onUk = pathname === "/uk" || pathname.startsWith("/uk/");
  const filterOrder: Filter[] = onUk
    ? ["all", "uk", "ireland"]
    : ["all", "ireland", "uk"];
  const orderedFilters = filterOrder.map(
    (id) => FILTERS.find((f) => f.id === id)!,
  );

  const filtered = clinics.filter((c) => {
    if (filter === "ireland") return c.country === "Ireland";
    if (filter === "uk") return c.country === "United Kingdom";
    return true;
  });

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = filtered.length - 1;
      setActiveIndex(closest);
      setIsScrollable(scroller.scrollWidth > scroller.clientWidth + 4);
    };

    scroller.scrollLeft = 0;
    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [filter, filtered.length]);

  const hasHeader = Boolean(blok.title || blok.body);

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,4.5rem)]">
        {hasHeader && (
          <div className="flex max-w-[520px] flex-col gap-3">
            {blok.title && (
              <h2
                className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                  onDark ? "text-white" : "text-purple-05"
                }`}
              >
                {blok.title}
              </h2>
            )}
            {blok.body && (
              <p
                className={`text-body leading-[1.6] ${onDark ? "text-white/85" : "text-black/65"}`}
              >
                {blok.body}
              </p>
            )}
          </div>
        )}

        {showFilters && (
          <div
            className={`flex flex-wrap items-center gap-2 ${
              hasHeader ? "mt-10 border-t border-black/10 pt-8" : ""
            }`}
          >
            {orderedFilters.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                aria-pressed={filter === f.id}
                className={`text-button inline-flex h-10 cursor-pointer items-center rounded-full px-4 font-semibold transition-colors ${
                  filter === f.id
                    ? "bg-purple-03 text-white"
                    : "text-purple-05 hover:bg-dusk-02 bg-white ring-1 ring-black/10"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        <ul
          ref={scrollerRef}
          className={`flex snap-x snap-mandatory gap-5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] sm:grid sm:snap-none sm:grid-cols-2 sm:gap-x-8 sm:gap-y-12 sm:overflow-visible md:grid-cols-3 [&::-webkit-scrollbar]:hidden ${
            showFilters ? "mt-8" : hasHeader ? "mt-8 lg:mt-12" : ""
          }`}
        >
          {filtered.map((clinic, i) => (
            <ClinicCard
              key={clinic.uuid}
              clinic={clinic}
              cardRef={(el) => {
                cardRefs.current[i] = el;
              }}
            />
          ))}
        </ul>

        {isScrollable && filtered.length > 1 && (
          <div className="mt-6 flex justify-center sm:hidden">
            <div className="flex items-center">
              {filtered.map((clinic, i) => (
                <button
                  key={clinic.uuid}
                  type="button"
                  onClick={() => {
                    cardRefs.current[i]?.scrollIntoView({
                      behavior: "smooth",
                      inline: "center",
                      block: "nearest",
                    });
                  }}
                  aria-label={`Go to clinic ${i + 1}`}
                  className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                >
                  <span
                    aria-hidden
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      activeIndex === i ? "bg-purple-03 w-8" : "bg-purple-01 w-2"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
